import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { makeStyles, tokens, Tooltip } from "@fluentui/react-components";
import { LinkDismissRegular, LinkEditRegular } from "@fluentui/react-icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { useImpulse } from "../hooks/transientStateHooks";
import { Button } from "./button";
import { ComboBox } from "./comboBox";
import { Link } from "./link";
const useStyles = makeStyles({
    linkDiv: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: tokens.spacingHorizontalS,
        minWidth: 0,
        overflow: "hidden",
    },
    link: {
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
});
/**
 * Displays a linked resource with one unlink-or-edit action, or a selector when unlinked.
 * @param props Resource identity, selection, navigation, and supported actions.
 * @returns The linked value or its replacement selector.
 */
export function ResourceSelector(props) {
    const { value, getEntities, getName, getId, filter, onSelect, onClear, onLink, disabled, ariaLabel, showEmptySelector } = props;
    const classes = useStyles();
    const comboBoxRef = useRef(null);
    const options = useMemo(() => getEntities()
        .filter((entity) => !filter || filter(entity))
        .map((entity) => ({ label: getName(entity)?.toString() || "", value: getId(entity) }))
        .sort((a, b) => a.label.localeCompare(b.label)), [getEntities, getName, getId, filter]);
    const [isEditing, setIsEditing] = useState(false);
    const [enteringEditMode, pulseEnteringEditMode] = useImpulse();
    useEffect(() => {
        if (enteringEditMode) {
            comboBoxRef.current?.focus();
        }
    }, [enteringEditMode]);
    if (value !== null && !isEditing) {
        return (_jsxs("div", { className: classes.linkDiv, children: [_jsx(Tooltip, { content: getName(value), relationship: "label", children: _jsx(Link, { className: classes.link, value: getName(value), onLink: onLink ? () => onLink(value) : undefined, "aria-label": onLink && ariaLabel ? `${ariaLabel}: open ${getName(value)}` : undefined }) }), onClear ? (_jsx(Tooltip, { content: "Unlink", relationship: "label", children: _jsx(Button, { icon: LinkDismissRegular, ariaLabel: ariaLabel ? `Clear ${ariaLabel}` : "Unlink", disabled: disabled, onClick: () => {
                            pulseEnteringEditMode(true);
                            onClear();
                        } }) })) : onSelect ? (_jsx(Tooltip, { content: "Edit Link", relationship: "label", children: _jsx(Button, { icon: LinkEditRegular, ariaLabel: ariaLabel ? `Change ${ariaLabel}` : "Edit Link", disabled: disabled, onClick: () => {
                            pulseEnteringEditMode(true);
                            setIsEditing(true);
                        } }) })) : undefined] }));
    }
    if (!onSelect && !showEmptySelector) {
        return _jsx(Link, { value: "None" });
    }
    return (_jsx(ComboBox, { ref: comboBoxRef, defaultOpen: enteringEditMode, label: "", ariaLabel: ariaLabel, disabled: disabled, options: options, value: value !== null ? getId(value) : "", onChange: (id) => {
            onSelect?.(getEntities().find((entity) => getId(entity) === id) ?? null);
            setIsEditing(false);
        } }));
}
ResourceSelector.displayName = "ResourceSelector";
//# sourceMappingURL=resourceSelector.js.map